"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MapPin, Calendar, Users, Trophy, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function EksperckaKadraIKlimatKohSamui() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    icon: <Trophy className="w-6 h-6 text-primary" />,
    title: "Dawid Kasperski",
    description: "Treningi pod okiem profesjonalnego zawodnika z ogromnym doświadczeniem."
  }, {
    icon: <Users className="w-6 h-6 text-primary" />,
    title: "Lamai Muay Thai",
    description: "Autentyczne sesje z lokalnymi, tajskimi trenerami w renomowanym klubie."
  }, {
    icon: <SafeHydrate><Calendar className="w-6 h-6 text-primary" /></SafeHydrate>,
    title: "12 Dni Balansu",
    description: "Starannie zaplanowany czas na morderczy trening, regenerację i zwiedzanie."
  }, {
    icon: <MapPin className="w-6 h-6 text-primary" />,
    title: "Koh Samui",
    description: "Egzotyczna wyspa jako tło dla Twojej sportowej transformacji i kulinarnych odkryć."
  }];
  return <section id="ekspercka-kadra-i-klimat-koh-samui" className="dark bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary/5 rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center relative z-10">
        
        {}
        <div ref={leftRef} className={`relative w-full aspect-square md:aspect-[4/3] lg:aspect-square transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
          {}
          <div className="absolute top-0 left-0 w-[60%] md:w-[55%] z-10 transform -rotate-6 hover:rotate-0 hover:z-40 transition-all duration-500 shadow-2xl">
            <div className="bg-white p-3 pb-12 md:p-4 md:pb-16 rounded-sm shadow-xl">
              <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                <img src="https://exact-tomcat.10web.cloud/wp-content/uploads/2026/03/IMG_5537-e1637045949372.jpg" alt="Trening w Lamai Muay Thai" className="object-cover w-full h-full contrast-125" />
              </div>
              <p className="text-black/70 font-handwriting text-center mt-4 text-sm md:text-base font-medium tracking-wide">
                Lamai Gym, Koh Samui
              </p>
            </div>
          </div>

          {}
          <div className="absolute bottom-[5%] right-0 w-[55%] md:w-[50%] z-20 transform rotate-6 hover:rotate-0 hover:z-40 transition-all duration-500 shadow-2xl">
            <div className="bg-white p-3 pb-12 md:p-4 md:pb-16 rounded-sm shadow-xl">
              <div className="relative aspect-square overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/authentic_thai_street_food.png" alt="Lokalne tajskie jedzenie" className="object-cover w-full h-full contrast-110 saturate-150" />
              </div>
              <p className="text-black/70 font-handwriting text-center mt-4 text-sm md:text-base font-medium tracking-wide">
                Regeneracja smakiem
              </p>
            </div>
          </div>

          {}
          <div className="absolute top-[20%] left-[20%] w-[65%] md:w-[60%] z-30 transform -rotate-2 hover:rotate-0 hover:z-40 transition-all duration-500 shadow-2xl">
            <div className="bg-white p-3 pb-12 md:p-4 md:pb-16 rounded-sm shadow-2xl">
              <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                <img src="https://exact-tomcat.10web.cloud/wp-content/uploads/2026/03/514220595_24342869295349446_7433641304548161971_n.jpg" alt="Dawid Kasperski w akcji" className="object-cover w-full h-full contrast-125" />
              </div>
              <p className="text-black/70 font-handwriting text-center mt-4 text-sm md:text-base font-medium tracking-wide">
                Elitarny poziom
              </p>
            </div>
          </div>

          {}
          <div className="absolute top-[5%] right-[-5%] w-[45%] md:w-[40%] z-15 transform -rotate-12 hover:rotate-0 hover:z-40 transition-all duration-500 shadow-2xl">
            <div className="bg-white p-3 pb-12 md:p-4 md:pb-16 rounded-sm shadow-xl">
              <div className="relative aspect-square overflow-hidden bg-muted">
                <img src="https://newwavecamp.com/wp-content/uploads/2026/03/468618651_18320973913095949_8577082515011612481_n.jpg" alt="Widok na wyspę Koh Samui" className="object-cover w-full h-full saturate-110" />
              </div>
              <p className="text-black/70 font-handwriting text-center mt-4 text-sm md:text-base font-medium tracking-wide">
                Wyspiarski klimat
              </p>
            </div>
          </div>

          {}
          <div className="absolute bottom-[-8%] left-[5%] w-[50%] md:w-[45%] z-25 transform rotate-9 hover:rotate-0 hover:z-40 transition-all duration-500 shadow-2xl">
            <div className="bg-white p-3 pb-12 md:p-4 md:pb-16 rounded-sm shadow-xl">
              <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                <img src="https://newwavecamp.com/wp-content/uploads/2026/03/37831851_2015910495138645_3056266154801627136_n.jpg" alt="Trening o zachodzie słońca" className="object-cover w-full h-full contrast-110" />
              </div>
              <p className="text-black/70 font-handwriting text-center mt-4 text-sm md:text-base font-medium tracking-wide"><br />Zwyciężaj</p>
            </div>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="bg-card/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] relative overflow-hidden group">
            {}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
            
            <div className="relative z-10">
              <h3 className="text-primary font-bold tracking-wider uppercase text-sm md:text-base mb-4 flex items-center gap-2">
                <span className="w-8 h-[2px] bg-primary inline-block"></span>
                Synergia Siły i Tradycji
              </h3>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-8 leading-tight">
                12 Dni Transformacji na <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Koh Samui</span>
              </h2>
              
              <div className="space-y-6 text-muted-foreground text-lg leading-relaxed mb-10">
                <p>
                  Dołącz do elitarnego obozu sportowego, gdzie nowoczesne techniki kickboxingu spotykają się z surową, autentyczną tradycją tajskiego boksu. To nie jest zwykły wyjazd – to kompleksowe doświadczenie zaprojektowane dla tych, którzy chcą przekroczyć własne granice.
                </p>
                <p>
                  Pod okiem profesjonalnego zawodnika, Dawida Kasperskiego, oraz doświadczonych tajskich trenerów z legendarnego klubu Lamai Muay Thai, przejdziesz przez starannie ułożony program. Zadbamy o każdy detal: od intensywnych sesji na macie, przez czas na eksplorację egzotycznej wyspy, aż po odkrywanie lokalnych, kulinarnych perełek, które zregenerują Twoje ciało.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                {features.map((feature, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                    <div className="bg-primary/10 p-3 rounded-xl shrink-0 border border-primary/20">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="text-foreground font-semibold mb-1">{feature.title}</h4>
                      <p className="text-sm text-muted-foreground leading-snug">{feature.description}</p>
                    </div>
                  </div>)}
              </div>

              <div className="pt-6 border-t border-white/10">
                <Button asChild size="lg" className="w-full sm:w-auto bg-gradient-to-r from-secondary to-chart-3 hover:from-chart-3 hover:to-secondary text-white border-0 shadow-lg shadow-secondary/20 group">
                  <Link to="#program">
                    Odkryj Pełny Program
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}